import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Seo from '../components/common/Seo';
import CategoryNav from '../components/common/CategoryNav';
import SearchBox from '../components/common/SearchBox';
import BlogList from '../components/blogs/BlogList';
import { collectionSchema } from '../utils/jsonLd';

/**
 * All articles, with search.
 *
 * The query lives in the URL (?q=…) so a search result is shareable and matches
 * the SearchAction advertised in the WebSite JSON-LD. The canonical URL drops
 * the query string (Seo does this), so search results never get indexed as
 * separate thin pages.
 */
function BlogsPage() {
  const [params] = useSearchParams();
  const query = (params.get('q') || '').trim();
  const [posts, setPosts] = useState([]);

  const onLoaded = useCallback((list) => setPosts(list), []);

  const title = query ? `Search: ${query}` : 'All Articles';
  const description = query
    ? `Articles with “${query}” in the title.`
    : 'Every story in one place — world events, business, technology, sport, entertainment, everyday life and more.';

  return (
    <div className="container page">
      <Seo
        title={title}
        path="/blogs"
        description={description}
        // A search result page should not compete with the real listing page.
        noIndex={Boolean(query)}
        jsonLd={
          query
            ? null
            : collectionSchema({
                name: 'All Articles',
                description,
                path: '/blogs',
                posts,
              })
        }
      />

      <header className="page-head">
        <h1 className="page-title">{query ? `Results for “${query}”` : 'All Articles'}</h1>
        <p className="page-sub">{description}</p>
        {query && (
          // Search matches titles only, so a reader who searched for a phrase
          // from inside an article needs to know why it found nothing.
          <p className="page-note">Search looks at article titles, not the full text.</p>
        )}
      </header>

      <SearchBox className="search-box-page" id="page-search" initialQuery={query} />

      <CategoryNav ariaLabel="Filter by category" />

      <BlogList
        query={query}
        onLoaded={onLoaded}
        emptyMessage={
          query
            ? `No article titles contain “${query}”. Try a shorter or more general word.`
            : 'No articles published yet.'
        }
      />
    </div>
  );
}

export default BlogsPage;
